import React, { Fragment, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Card, CardBody, CardFooter, CardHeader, Col, FormGroup, Input, Label, Row } from "reactstrap";
import MultiSearchPanel from "../Search/MultiSearchPanel";
import { useService } from "../../context/ServiceContext";
import { useDropzone } from "react-dropzone";
import LoadedContent from "../LoadedContent";
import { fireAlert } from "../../utils/alerts";
import ErrorAlertContent from "../ErrorAlertContent";
import { ExistingCertificateSummary } from "../../views/Certificates/Edit";

function CertificateSeriesProductSearchResults({ search, keyCompany, saveCertificate, updateCertificate, previewCertificate, history, cloneData, existingCertificate }) {
  const intl = useIntl();

  const { certificateSeriesProductsProvider, certificateRegulationsProvider, certificateLegislationsProvider, certificateGenerateProvider } = useService();

  const [error, setError] = useState();

  const [hasSearched, setHasSearched] = useState(false);

  const [customSeriesName, setCustomSeriesName] = useState(null);
  const [seriesData, setSeriesData] = useState();
  const [productData, setProductData] = useState();

  const [selectedSeries, setSelectedSeries] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [originalLegislations, setOriginalLegislations] = useState([]);
  const [legislations, setLegislations] = useState([]);
  const [regulations, setRegulations] = useState([]);
  const [displayRegulations, setDisplayRegulations] = useState([]);

  const [tickedLegislations, setTickedLegislations] = useState([]);
  const [selectedLegislations, setSelectedLegislations] = useState([]);
  const [selectedRegulations, setSelectedRegulations] = useState([]);

  const [notes, setNotes] = useState("");

  const [clonedCertificateArchives, setClonedCertificateArchives] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const convertDay = (date) => {
    const today = date ?? new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(convertDay);
  const [endDate, setEndDate] = useState(convertDay);
  const [noEndDate, setNoEndDate] = useState(true);

  useEffect(() => {
    if (!search) return;

    //setSelectedSeries([]);
    //setSelectedProducts([]);

    loadData();
  }, [search, keyCompany]);

  useEffect(() => {
    async function getData() {
      try {
        const legislations = await certificateLegislationsProvider.get();
        setOriginalLegislations(legislations);

        const sources = [existingCertificate?.regs, cloneData?.options.includeRegulations ? cloneData?.regs : null].filter(Boolean);

        for (const src of sources) {
          const clonedLegislations = src.filter((r) => r.idTipoRegulacion == 2 || r.idTipoRegulacion == 4);
          for (const clonedLegislation of clonedLegislations) {
            if (!legislations.some((r) => r.idRegulacion == clonedLegislation.idRegulacion)) {
              legislations.push({ ...clonedLegislation, missing: true });
            }
          }
        }

        setLegislations(legislations);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    getData();
  }, []);

  useEffect(() => {
    async function getData() {
      try {
        const regulations = await certificateRegulationsProvider.get(selectedLegislations);

        const sources = [existingCertificate?.regs, cloneData?.options.includeRegulations ? cloneData?.regs : null].filter(Boolean);

        for (const src of sources) {
          const clonedRegulations = src.filter((r) => r.idTipoRegulacion == 3);
          for (const clonedRegulation of clonedRegulations) {
            if (!regulations.some((r) => r.idRegulacion == clonedRegulation.idRegulacion)) {
              regulations.push({ ...clonedRegulation, missing: true });
            }
          }
        }

        setSelectedRegulations(selectedRegulations.filter((s) => regulations.some((r) => r.idRegulacion == s)));

        setRegulations(regulations);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    getData();
  }, [selectedLegislations]);

  useEffect(() => {
    async function getData() {
      try {
        const displayRegulations = await certificateRegulationsProvider.get(tickedLegislations);
        setDisplayRegulations(displayRegulations);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    getData();
  }, [tickedLegislations]);

  useEffect(() => {
    if (!cloneData && !existingCertificate) return;

    const regs = [existingCertificate?.regs, cloneData?.options.includeRegulations ? cloneData?.regs : null].filter(Boolean);

    if (regs?.length > 0) {
      setSelectedLegislations(regs[0].filter((r) => r.idTipoRegulacion == 2 || (r.idTipoRegulacion == 4 && r.idRegulacionParent == null)).map((r) => r.idRegulacion));
      setSelectedRegulations(regs[0].filter((r) => r.idTipoRegulacion == 3 || (r.idTipoRegulacion == 4 && r.idRegulacionParent != null)).map((r) => r.idRegulacion));
    }

    const source = cloneData?.options?.includeValidity ? cloneData : existingCertificate;

    if (source?.validFrom != null) {
      setStartDate(convertDay(new Date(source.validFrom)));
    }

    if (source) {
      if (source.validTo != null) {
        setEndDate(convertDay(new Date(source.validTo)));
        setNoEndDate(false);
      } else {
        setNoEndDate(true);
      }
    }

    const pdfSource = cloneData?.options?.includePDFS && cloneData?.uploadedCertificates?.length > 0 ? cloneData.uploadedCertificates : existingCertificate?.uploadedCertificates;

    if (pdfSource?.length > 0) {
      setClonedCertificateArchives(pdfSource);
    }

    if (existingCertificate) {
      setNotes(existingCertificate?.notes ?? "");
    }
  }, [cloneData, existingCertificate]);

  async function onSeriesClick(serie) {
    let series = [...selectedSeries];
    if (series.some((s) => s.codSerieComercial == serie.codSerieComercial)) series = series.filter((s) => s.codSerieComercial != serie.codSerieComercial);
    else series = series.concat(serie);

    // if (series.includes(seriesId)) series = series.filter((s) => s != seriesId);
    // else series = series.concat(seriesId);

    setSelectedSeries(series);
  }

  async function onProductClick(product) {
    let products = [...selectedProducts];
    if (products.some((p) => p.codProductoComercial == product.codProductoComercial)) products = products.filter((p) => p.codProductoComercial != product.codProductoComercial);
    else products = products.concat(product);

    setSelectedProducts(products);
  }

  async function onLegislationClick(id, remove) {
    if (remove) {
      setSelectedLegislations(selectedLegislations.filter((s) => s != id));
      setTickedLegislations(tickedLegislations.filter((s) => s != id));
    } else {
      let legislation = [...tickedLegislations];
      if (legislation.includes(id)) legislation = legislation.filter((s) => s != id);
      else {
        legislation = legislation.concat(id);

        let selectedLegislation = [...selectedLegislations];
        if (!selectedLegislation.includes(id)) {
          selectedLegislation = selectedLegislation.concat(id);
          setSelectedLegislations(selectedLegislation);
        }
      }
      setTickedLegislations(legislation);
    }
  }

  async function onRegulationClick(id) {
    let regulations = [...selectedRegulations];
    if (regulations.includes(id)) regulations = regulations.filter((s) => s != id);
    else regulations = regulations.concat(id);

    setSelectedRegulations(regulations);
  }

  async function loadData() {
    try {
      const seriesProducts = await certificateSeriesProductsProvider.get({ keyCompany, search });

      const distinctPairs = [
        ...new Set(
          seriesProducts.map((item) =>
            JSON.stringify({
              shortNameComercial: item.shortNameComercial,
              codSerieComercial: item.codSerieComercial,
            }),
          ),
        ),
      ].map((str) => JSON.parse(str));

      setHasSearched(true);
      setSeriesData(distinctPairs);
      setProductData(seriesProducts);
    } catch (error) {
      setSeriesData(null);
      setProductData(null);

      fireAlert(
        <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error",
      );
    }
  }

  function selectAll() {
    const products = [...selectedProducts];

    productData
      .filter((p) => selectedSeries?.some((s) => s.codSerieComercial == p.codSerieComercial))
      .forEach((product) => {
        if (products.some((p) => p.codProductoComercial == product.codProductoComercial) == false) {
          products.push(product);
        }
      });

    setSelectedProducts(products);
  }

  function deselectAll() {
    const products = [...selectedProducts];
    for (let i = 0; i < productData.length; i++) {
      const product = productData[i];
      if (products.some((p) => p.codProductoComercial == product.codProductoComercial)) {
        products.push(product);
      }
    }
    const sp = productData.map((p) => p.codProductoComercial);
    setSelectedProducts(products.filter((p) => !sp.includes(p.codProductoComercial)));
  }

  const [files, setFiles] = useState([]);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      // Update the state with new files
      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    },
  });

  const removeFile = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.path !== fileToRemove.path));
  };

  const fileItems = files.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
      <button className="btn btn-sm btn-primary ml-2 mb-2" onClick={() => removeFile(file)}>
        Remove
      </button>
    </li>
  ));

  async function customSeries() {
    const { value: series } = await fireAlert(
      intl.formatMessage({ id: "certificates.enter_name_of_new_type", defaultMessage: "Please enter the name of the new type" }),
      intl.formatMessage({ id: "certificates.create_a_type", defaultMessage: "Create a type" }),
      null,
      { input: "text" },
    );
    if (series != null && series.trim() != "") {
      setCustomSeriesName(series);
    } else {
      setCustomSeriesName(null);
    }
  }

  async function update() {
    setIsLoading(true);

    try {
      const selectedLegislationsFull = legislations.filter((l) => selectedLegislations.includes(l.idRegulacion));
      const selectedRegulationsFull = regulations.filter((l) => selectedRegulations.includes(l.idRegulacion));

      await updateCertificate({
        files: files,
        legislations: selectedLegislationsFull,
        regulations: selectedRegulationsFull,
        startDate: startDate,
        endDate: noEndDate ? "" : endDate,
        clonedUploads: clonedCertificateArchives,
        notes: notes,
      });

      fireAlert(
        intl.formatMessage({ id: "certificates.successfully_updated_certificate", defaultMessage: "Successfully updated declaration" }),
        intl.formatMessage({ id: "app.success", defaultMessage: "Success" }),
        "success",
      ).then(() => {
        history.push(`/certificates`);
      });
    } catch (error) {
      fireAlert(
        <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error",
      );
    }

    setIsLoading(false);
  }

  async function save() {
    setIsLoading(true);

    try {
      const selectedLegislationsFull = legislations.filter((l) => selectedLegislations.includes(l.idRegulacion));
      const selectedRegulationsFull = regulations.filter((l) => selectedRegulations.includes(l.idRegulacion));

      await saveCertificate({
        files: files,
        keyCompany: keyCompany != null && keyCompany != "" ? keyCompany : -1,
        customType: customSeriesName ?? "",
        series: selectedSeries,
        products: selectedProducts,
        legislations: selectedLegislationsFull,
        regulations: selectedRegulationsFull,
        startDate: startDate,
        endDate: noEndDate ? "" : endDate,
        clonedUploads: clonedCertificateArchives,
        notes: notes,
      });

      fireAlert(
        intl.formatMessage({ id: "certificates.successfully_created_certificate", defaultMessage: "Successfully created declaration" }),
        intl.formatMessage({ id: "app.success", defaultMessage: "Success" }),
        "success",
      ).then(() => {
        history.push(`/certificates`);
      });
    } catch (error) {
      fireAlert(
        <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error",
      );
    }

    setIsLoading(false);
  }

  async function preview() {
    setIsLoading(true);

    try {
      const selectedLegislationsFull = legislations.filter((l) => selectedLegislations.includes(l.idRegulacion));
      const selectedRegulationsFull = regulations.filter((l) => selectedRegulations.includes(l.idRegulacion));

      const pdfBytes = await previewCertificate({
        keyCompany: keyCompany != null && keyCompany != "" ? keyCompany : -1,
        customType: customSeriesName ?? "",
        series: selectedSeries,
        products: selectedProducts,
        legislations: selectedLegislationsFull,
        regulations: selectedRegulationsFull,
        startDate: startDate,
        endDate: noEndDate ? "" : endDate,
        notes: notes,
      });

      const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      link.download = "preview.pdf";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      fireAlert(
        <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error",
      );
    }

    setIsLoading(false);
  }

  async function generate(keyCompany, series, language, templateId, idTipoDocumental, pdfName) {
    setIsLoading(true);

    try {
      const pdfBytes = await certificateGenerateProvider.get({
        keyCompany: keyCompany,
        series: series,
        language: language,
        templateId: templateId,
        idTipoDocumental: idTipoDocumental,
        nameOfCertificate: pdfName,
      });

      const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      link.download = pdfName;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  }

  return (
    <LoadedContent loading={isLoading} overlay={true}>
      {hasSearched && productData?.length == 0 && customSeriesName == null && (
        <Card>
          <CardBody>
            <FormattedMessage id="certificates.no_products_found" defaultMessage="No products found that match that search. Would you like to create a type?" />
          </CardBody>
          <CardFooter>
            <button type="button" className="btn btn-outline-danger" onClick={customSeries}>
              <FormattedMessage id="certificates.create_a_type" defaultMessage="Create a type" />
            </button>
          </CardFooter>
        </Card>
      )}

      {(seriesData?.length > 0 || productData?.length > 0) && (
        <Card>
          <CardHeader>
            <FormattedMessage id="certificates.product_selection" defaultMessage="Product selection" />
          </CardHeader>
          <CardBody>
            <Row>
              <Col sm={6}>
                <h3>
                  <FormattedMessage id="app.series" defaultMessage="Series" />
                </h3>
                <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                  {seriesData
                    ?.filter((s) => s.shortNameComercial)
                    .map((s) => (
                      <div key={s.codSerieComercial} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedSeries.some((s1) => s1.codSerieComercial == s.codSerieComercial)}
                          id={`check-series-${s.codSerieComercial}`}
                          onClick={() => onSeriesClick(s)}
                        />
                        <label className="form-check-label" for={`check-series-${s.codSerieComercial}`}>
                          {s.shortNameComercial}
                        </label>
                      </div>
                    ))}
                </div>
              </Col>
              <Col sm={6}>
                {productData && (
                  <>
                    <h3 style={{ display: "inline-block" }}>
                      <FormattedMessage id="certificates.products" defaultMessage="Products" />
                    </h3>
                    <button className="btn btn-link btn-sm" onClick={selectAll}>
                      <FormattedMessage id="app.select_all" defaultMessage="Select all" />
                    </button>
                    <button className="btn btn-link btn-sm" onClick={deselectAll}>
                      <FormattedMessage id="app.deselect_all" defaultMessage="Deselect all" />
                    </button>
                    <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                      {productData
                        ?.filter((p) => p.productoComercial && selectedSeries?.some((s) => s.codSerieComercial == p.codSerieComercial))
                        .map((p) => (
                          <div key={p.codProductoComercial} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedProducts.some((p1) => p1.codProductoComercial == p.codProductoComercial)}
                              id={`check-product-${p.codProductoComercial}`}
                              onClick={() => onProductClick(p)}
                            />
                            <label className="form-check-label" for={`check-product-${p.codProductoComercial}`}>
                              {p.productoComercial}
                            </label>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}
      {(selectedSeries?.length > 0 || selectedProducts?.length > 0 || customSeriesName != null || existingCertificate) && (
        <>
          <Row>
            {customSeriesName != null && (
              <Col sm={6}>
                <Card className="mt-3">
                  <CardHeader>Creating a new type</CardHeader>
                  <CardBody>
                    {customSeriesName}{" "}
                    <Button type="button" className="btn btn-link text-dark ml-3" onClick={() => setCustomSeriesName(null)}>
                      <i className="fas fa-trash-alt"></i>
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            )}

            <Col sm="6">
              <Card className="mt-3">
                <CardHeader>
                  <FormattedMessage id="certificates.upload_certificates" defaultMessage="Upload Certificates" />
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col>
                      <Row>
                        <Col>
                          {files.length > 0 && (
                            <>
                              <h3 className="card-title">
                                <FormattedMessage id="certificates.files_to_upload" defaultMessage="Files to upload" />
                              </h3>
                              <ul>{fileItems}</ul>
                            </>
                          )}
                          <div {...getRootProps({ className: "dropzone" })}>
                            <input {...getInputProps()} />
                            <button type="button" onClick={open} className="btn btn-outline-dark">
                              <FormattedMessage id="app.add" defaultMessage="Add" />
                            </button>
                          </div>

                          {clonedCertificateArchives?.length > 0 && (
                            <>
                              <hr></hr>
                              <div className="mt-4">
                                <h3 className="card-title">
                                  {existingCertificate ? (
                                    <FormattedMessage id="certificates.existing_pdfs" defaultMessage="Existing PDFs" />
                                  ) : (
                                    <FormattedMessage id="certificates.cloned_pdfs" defaultMessage="Cloned PDFs" />
                                  )}
                                </h3>
                                <ul>
                                  {clonedCertificateArchives.map((c) => (
                                    <li key={c.pdf}>
                                      {c.pdf}
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary ml-2 mb-2"
                                        onClick={() => setClonedCertificateArchives(clonedCertificateArchives.filter((cc) => cc.pdf != c.pdf))}
                                      >
                                        <FormattedMessage id="app.remove" defaultMessage="Remove" />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary mb-2"
                                        onClick={() => {
                                          if (existingCertificate) {
                                            generate(existingCertificate.company, existingCertificate.series, existingCertificate.languageId, 0, c.idTipoDocumental, c.pdf);
                                          } else {
                                            generate(cloneData.company, cloneData.series, cloneData.language, 0, c.idTipoDocumental, c.pdf);
                                          }
                                        }}
                                      >
                                        <FormattedMessage id="app.view" defaultMessage="View" />
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {customSeriesName == null && existingCertificate == null && (
              <Col sm="6">
                <Card className="mt-3">
                  <CardHeader className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <FormattedMessage id="certificates.selected_products" defaultMessage="Selected Products" />
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => {
                        setSelectedSeries([]);
                        setSelectedProducts([]);
                      }}
                    >
                      <FormattedMessage id="app.remove_all" defaultMessage="Remove all" />
                    </button>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col>
                        {selectedSeries
                          .filter((s) => selectedProducts.some((p) => p.codSerieComercial == s.codSerieComercial) == false)
                          .map((p) => (
                            <Fragment key={p.id}>
                              <span className="badge badge-pill badge-primary">
                                {p.shortNameComercial} <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onSeriesClick(p)}></i>
                              </span>{" "}
                            </Fragment>
                          ))}
                        {selectedProducts.map((p) => (
                          <Fragment key={p.id}>
                            <span className="badge badge-pill badge-primary">
                              {p.productoComercial} <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onProductClick(p)}></i>
                            </span>{" "}
                          </Fragment>
                        ))}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
          <Row>
            <Col>
              <Card className="mt-3">
                <CardHeader>
                  <FormattedMessage id="certificates.define_regulations" defaultMessage="Define Regulations" />
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col sm="6">
                      <h3>
                        <FormattedMessage id="certificates.legislations" defaultMessage="Legislations" />
                      </h3>
                      <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                        {legislations.map((s) => (
                          <div key={s.idRegulacion} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={tickedLegislations.includes(s.idRegulacion)}
                              id={`check-legislation-${s.idRegulacion}`}
                              onClick={() => onLegislationClick(s.idRegulacion)}
                            />
                            <label className={`form-check-label ${s.missing ? "text-primary" : ""}`} for={`check-legislation-${s.idRegulacion}`}>
                              {s.regulacion} - {s.regulacionDescripcion} {s.missing ? <i className="fas fa-exclamation-circle"></i> : null}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4"></div>
                    </Col>
                    <Col sm="6">
                      <h3>
                        <FormattedMessage id="certificates.regulations" defaultMessage="Regulations" />
                      </h3>
                      <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                        {displayRegulations?.map((s) => (
                          <div key={s.idRegulacion} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedRegulations.includes(s.idRegulacion)}
                              id={`check-regulation-${s.idRegulacion}`}
                              onClick={() => onRegulationClick(s.idRegulacion)}
                            />
                            <label className={`form-check-label ${s.missing ? "text-primary" : ""}`} for={`check-regulation-${s.idRegulacion}`}>
                              {s.regulacion} - {s.regulacionDescripcion} {s.missing ? <i className="fas fa-exclamation-circle"></i> : null}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4"></div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter>
                  <Row>
                    <Col sm="6">
                      {selectedLegislations.map((s) => (
                        <Fragment key={s.idRegulacion}>
                          <span className="badge badge-pill badge-primary">
                            {legislations.find((r) => r.idRegulacion == s)?.regulacion}{" "}
                            <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onLegislationClick(s, true)}></i>
                          </span>{" "}
                        </Fragment>
                      ))}
                    </Col>
                    <Col sm="6">
                      {selectedRegulations.map((s) => (
                        <Fragment key={s.idRegulacion}>
                          <span className="badge badge-pill badge-primary">
                            {regulations.find((r) => r.idRegulacion == s)?.regulacion}{" "}
                            <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onRegulationClick(s)}></i>
                          </span>{" "}
                        </Fragment>
                      ))}
                    </Col>
                  </Row>
                </CardFooter>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card className="mt-3 mb-3">
                <CardBody>
                  <div className="form-group row mb-0 pb-0">
                    <label htmlFor="name" className="col-sm-2 col-form-label">
                      <FormattedMessage id="certificates.start_date" defaultMessage="Start date" />
                    </label>
                    <div className="col-sm-2">
                      <input
                        type="date"
                        id="dateInput"
                        value={startDate}
                        className="form-control"
                        onChange={(e) => {
                          const dateValue = e.target.value;
                          setStartDate(dateValue);
                        }}
                      />
                    </div>
                    <label htmlFor="name" className="col-sm-2 col-form-label">
                      <FormattedMessage id="certificates.end_date" defaultMessage="End date" />
                    </label>
                    <div className="col-sm-2">
                      <input
                        type="date"
                        id="dateInput"
                        value={endDate}
                        className="form-control"
                        disabled={noEndDate}
                        onChange={(e) => {
                          const dateValue = e.target.value;
                          setEndDate(dateValue);
                        }}
                      />
                    </div>
                    <div className="col-sm-2" style={{ display: "flex", alignItems: "center" }}>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="no-expiry" checked={noEndDate} onChange={(e) => setNoEndDate(e.target.checked)} />
                        <label className="form-check-label " for="no-expiry">
                          <FormattedMessage id="certificates.no_expiry" defaultMessage="No expiration date" />
                        </label>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card className="mt-3 mb-3">
                <CardBody>
                  <FormGroup row className="my-0">
                    <Label sm={3}>
                      <FormattedMessage id="certificates.notes" defaultMessage="Notes" />
                    </Label>
                    <Col sm={9}>
                      <Input type="textarea" height={50} defaultValue={notes} onKeyUp={(e) => setNotes(e.target.value)}></Input>
                    </Col>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              {existingCertificate && (
                <button type="button" className="btn btn-outline-danger" onClick={update}>
                  <FormattedMessage id="app.update" defaultMessage="Update" />
                </button>
              )}
              {!existingCertificate && (
                <>
                  <button type="button" className="btn btn-outline-danger" onClick={save}>
                    <FormattedMessage id="app.save" defaultMessage="Save" />
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={preview}>
                    <FormattedMessage id="app.preview" defaultMessage="Preview" />
                  </button>
                </>
              )}
            </Col>
          </Row>
        </>
      )}
    </LoadedContent>
  );
}

export default CertificateSeriesProductSearchResults;
